import { FSMessage } from "src/common/keys/protos/collections_pb";
import { Notification as FSNotification } from "src/common/keys/protos/async_job_pb";
import {
  DriveSocket, DriveRequest, Message, DriveErrorMessages, MessageHandlerDisplayType, WebsocketConnectionRoutes, MessageAnchors, ConfigKeys
} from "src/common";
import { MessageRequest, WebsocketStatus, WebsocketStatusType, websocketActions, uiActions, AsyncJobRequest, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";
import { filesyncSocketAPI as api, WebSocketReconnect } from "../../base";

// Drive Websocket Connections
let drive_socket: DriveSocket;
let notification_socket: DriveSocket;
let upload_socket: DriveSocket;
let fs_notification_socket: DriveSocket;
let watch_socket: DriveSocket;

// --------------------------------------------------------------------------------------------
// RTK Queries Hooks: Open API generated endpoints and types 
// --------------------------------------------------------------------------------------------
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    initializeDriveHooks: build.query<MessageRequest[], string>({
      queryFn: () => ({ data: [] }),
      async onCacheEntryAdded(
        arg,
        { dispatch, cacheDataLoaded, cacheEntryRemoved, updateCachedData }
      ) {
        await cacheDataLoaded;

        let socket: WebSocket | null = null;
        let message_listener: ((event: MessageEvent) => void) | null = null;

        // Reconnection logic
        const reconnect = new WebSocketReconnect();

        function connect() {
          // Create a websocket connection when the cache subscription starts
          drive_socket = new DriveSocket();
          socket = drive_socket.socket;

          try {
            // When data is received from the socket, update our query result
            message_listener = (event: MessageEvent) => {
              const data = event.data as ArrayBuffer;
              const serialized = new Uint8Array(data);
              const message = FSMessage.deserializeBinary(serialized);
              const request_id = message.getRequestId_asB64();
              const type = message.getType();

              updateCachedData((request: MessageRequest[]) => {
                request.push({
                  id: request_id,
                  message,
                  type,
                });
              });
            };

            socket.addEventListener("message", message_listener);

            socket.addEventListener("open", () => {
              if (!!socket && socket.readyState === WebSocket.OPEN) {
                drive_socket.status = WebsocketStatus.connected;
                dispatch(websocketActions.wsDriveStatus(WebsocketStatus.connected));
                reconnect.resetBackoff();
                // NOTE: ping/pong is handled by the server (CollectionServer.API.WS.Collection),
                // so we do NOT send any custom ping frames from the browser.
              }
            });

            socket.addEventListener("close", () => {
              drive_socket.status = WebsocketStatus.disconnected;
              dispatch(websocketActions.wsDriveStatus(WebsocketStatus.disconnected));

              // Handle reconnections, unless explicitly closed
              if (!reconnect.manually_closed) {
                const delay = reconnect.getNextDelay();
                reconnect.timeout = setTimeout(connect, delay);
              }
            });

            socket.addEventListener("error", (event: Event) => {
              dispatch(
                uiActions.handleRequestErrors(
                  new Message(
                    DriveErrorMessages.error_drive_socket,
                    MessageHandlerDisplayType.logger
                  ),
                  {
                    stack_message: DriveErrorMessages.error_drive_socket,
                    stack_error: event,
                  }
                )
              );
            });
          } catch (stack_error: unknown) {
            dispatch(
              uiActions.handleRequestErrors(
                new Message(
                  DriveErrorMessages.default,
                  MessageHandlerDisplayType.logger
                ),
                {
                  stack_message: DriveErrorMessages.error_drive_socket,
                  stack_error,
                }
              )
            );
          }
        }

        // Connect websocket on init
        connect();

        // When the cache subscription is removed, clean up
        await cacheEntryRemoved;

        reconnect.manually_closed = true;
        !!reconnect.timeout && clearTimeout(reconnect.timeout);

        if (socket && message_listener) {
          (socket as WebSocket).removeEventListener("message", message_listener);
        }

        (socket as WebSocket | null)?.close();
      },
      providesTags: ["collection_ws"],
    }),
    sendRequest: build.mutation<DriveRequest, DriveRequest>({
      queryFn: (data, api) => {
        const socket = drive_socket?.socket;
        if (!!socket && socket.readyState === WebSocket.OPEN) {
          socket.send(data.envelope);
          // Add DriveRequest to state
          const callbacks = (api.getState() as RootState).websocket.drive_ws_callbacks;
          const new_callbacks = callbacks.slice();
          new_callbacks.push(data);
          api.dispatch(websocketActions.setWsDriveCallbacks(new_callbacks));
        } else {
          api.dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger),
            {
              stack_message: DriveErrorMessages.error_socket_not_ready.replace(MessageAnchors.actionType, "drive_socket"),
              stack_error: { websocket_readyState: socket.readyState }
            }));
        }

        return { data };
      }
    }),
    closeSocket: build.mutation<WebsocketStatusType, any>({
      queryFn: (data, api) => {
        const status = (api.getState() as RootState).websocket.drive_ws_status;
        if (!!drive_socket && (status === WebsocketStatus.connected || drive_socket.socket.readyState === WebSocket.OPEN)) {
          drive_socket.socket.close();
          api.dispatch(websocketActions.wsDriveStatus(WebsocketStatus.disconnecting));
        }
        return { data };
      }
    }),

    // --------------------------------------------------------------------------------
    // Collection Server Filesync Notification Socket
    // https://github.com/PreVeil/core/blob/dev/collection_server/docs/filesync_notifications.md
    // --------------------------------------------------------------------------------
    initializeNotificationHooks: build.query<MessageRequest | null, string>({
      queryFn: () => ({ data: null }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        await cacheDataLoaded;
        let socket: WebSocket | null = null;
        //  Reconnection logic
        const reconnect = new WebSocketReconnect();
        // NOTE: connect websocket to server
        function connect() {
          // create a websocket connection when the cache subscription starts
          notification_socket = new DriveSocket();
          socket = notification_socket.socket;
          try {
            // NOTE: when data is received from the socket connection to the server,update our query result with the received message
            const listener = (event: MessageEvent) => {
              const data = event.data as ArrayBuffer;
              const serialized = new Uint8Array(data);
              const message = FSMessage.deserializeBinary(serialized);
              const request_id = message.getRequestId_asB64();
              const type = message.getType();
              updateCachedData(() => {
                return {
                  id: request_id,
                  message,
                  type
                }
              })
            }
            socket.addEventListener("message", listener);
            socket.addEventListener("open", () => {
              if (!!socket && socket.readyState === WebSocket.OPEN) {
                notification_socket.status = WebsocketStatus.connected;
                dispatch(websocketActions.wsDriveNotificationStatus(WebsocketStatus.connected));
                reconnect.resetBackoff();
              }
            });
            socket.addEventListener("close", () => {
              notification_socket.status = WebsocketStatus.disconnected;
              dispatch(websocketActions.wsDriveNotificationStatus(WebsocketStatus.disconnected));
              // NOTE: Handle reconnections
              if (!reconnect.manually_closed) {
                const delay = reconnect.getNextDelay();
                reconnect.timeout = setTimeout(connect, delay);
              }
            });
            socket.addEventListener("error", (event: Event) => {
              dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_notification_socket, MessageHandlerDisplayType.logger), {
                stack_message: DriveErrorMessages.error_notification_socket,
                stack_error: event
              }));
            });
          } catch (stack_error: unknown) {
            dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger), { stack_message: DriveErrorMessages.error_notification_socket, stack_error }));
          }
        }
        // NOTE: Connect websocket on init
        connect();
        await cacheEntryRemoved;

        // NOTE: Remove logic for websocket reconnection
        reconnect.manually_closed = true;
        !!reconnect.timeout && clearTimeout(reconnect.timeout);
        (socket as WebSocket | null)?.close();
      },
    }),
    sendNotificationRequest: build.mutation<DriveRequest, DriveRequest>({
      queryFn: (data, api) => {
        const socket = notification_socket?.socket;
        (!!socket && socket.readyState === WebSocket.OPEN) ? socket.send(data.envelope) :
          api.dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger), {
            stack_message: DriveErrorMessages.error_notification_socket_not_ready,
            stack_error: { websocket_readyState: socket.readyState }
          }));
        return { data };
      }
    }),
    closeNotificationSocket: build.mutation<string, string>({
      queryFn: (data, api) => {
        const status = (api.getState() as RootState).websocket.drive_notification_ws_status;
        if (!!notification_socket && (status === WebsocketStatus.connected || notification_socket.socket.readyState === WebSocket.OPEN)) {
          notification_socket.socket.close();
          api.dispatch(websocketActions.wsDriveNotificationStatus(WebsocketStatus.disconnecting));
        }
        return { data };
      }
    }),
    // --------------------------------------------------------------------------------
    // Drive Upload dedicated socket
    // --------------------------------------------------------------------------------
    initializeUploadHooks: build.query<MessageRequest | null, any>({
      queryFn: () => ({ data: null }),
      async onCacheEntryAdded(arg, { dispatch, getState, cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        // create a websocket connection when the cache subscription starts
        upload_socket = new DriveSocket();
        const socket = upload_socket.socket;
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;
          // NOTE: when data is received from the socket connection to the server,update our query result with the received message
          const listener = (event: MessageEvent) => {
            const data = event.data as ArrayBuffer;
            const serialized = new Uint8Array(data);
            const message = FSMessage.deserializeBinary(serialized);
            const request_id = message.getRequestId_asB64();
            const type = message.getType();
            updateCachedData(() => {
              return {
                id: request_id,
                message,
                type
              };
            })
          }
          socket.addEventListener("message", listener);
          socket.addEventListener("open", () => {
            if (socket.readyState === WebSocket.OPEN) {
              upload_socket.status = WebsocketStatus.connected;
              dispatch(websocketActions.wsDriveUploadStatus(WebsocketStatus.connected));
              // Note: If socket closed mid-upload we need to pick up where it last left off
              const pending_callback = (getState() as RootState).websocket.drive_upload_ws_callback;
              !!pending_callback && socket.send(pending_callback.envelope);
            }
          });
          socket.addEventListener("close", (event: CloseEvent) => {
            if (event.code === 1006) {
              // Note: Indicates the connection closed due to an abnormal error and must be reopened
              dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger), { error: event }));
            }
            upload_socket.status = WebsocketStatus.disconnected;
            dispatch(websocketActions.wsDriveUploadStatus(WebsocketStatus.disconnected));
          });
          socket.addEventListener("error", (event: Event) => {
            dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_upload_socket, MessageHandlerDisplayType.logger),
              {
                stack_message: DriveErrorMessages.error_upload_socket,
                stack_error: event
              }));
          });
        } catch (stack_error: unknown) {
          dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger),
            { stack_message: DriveErrorMessages.error_upload_socket, stack_error }));
        }
        await cacheEntryRemoved;
        socket.close();
      },
    }),
    sendUploadRequest: build.mutation<DriveRequest, DriveRequest>({
      queryFn: (data, api) => {
        const socket = upload_socket.socket;
        if (!!socket && socket.readyState === WebSocket.OPEN) {
          socket.send(data.envelope);
          api.dispatch(websocketActions.setWsDriveUploadCallback(data));
        } else {
          if (!!socket && socket.readyState === WebSocket.CONNECTING) {
            // Note: If socket is connecting, we need to store the request and try again once connected
            api.dispatch(websocketActions.setWsDriveUploadCallback(data));
          } else {
            api.dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger),
              {
                stack_message: DriveErrorMessages.error_socket_not_ready.replace(MessageAnchors.actionType, "upload_socket"),
                stack_error: { websocket_readyState: socket.readyState }
              }));
          }
        }
        return { data };
      }
    }),
    closeUploadSocket: build.mutation<WebsocketStatusType, WebsocketStatusType>({
      queryFn: (data, api) => {
        if (upload_socket.status === WebsocketStatus.connected) {
          upload_socket.socket.close();
          api.dispatch(websocketActions.wsDriveUploadStatus(WebsocketStatus.disconnecting));
        }
        return { data };
      }
    }),

    // --------------------------------------------------------------------------------
    // LOCAL Filesync Notification Socket dedicated socket
    // --------------------------------------------------------------------------------
    initializeFSNotificationHooks: build.query<AsyncJobRequest | null, string>({
      queryFn: () => ({ data: null }),
      async onCacheEntryAdded(arg, { dispatch, cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        // wait for the initial query to resolve before proceeding
        await cacheDataLoaded;
        let socket: WebSocket | null = null;
        //  Reconnection logic
        const reconnect = new WebSocketReconnect();
        // NOTE: connect websocket to server
        function connect() {
          // create a websocket connection when the cache subscription starts
          fs_notification_socket = new DriveSocket(`${getConfigValue(ConfigKeys.filesync_websocket_server)}/${WebsocketConnectionRoutes.subscribe_all}`);
          socket = fs_notification_socket.socket;
          try {
            // NOTE: when data is received from the socket connection to the server,update our query result with the received message
            const listener = (event: MessageEvent) => {
              const data = event.data as ArrayBuffer;
              const serialized = new Uint8Array(data);
              const message = FSNotification.deserializeBinary(serialized);
              const type = message.getType();
              updateCachedData(() => {
                return {
                  message,
                  type
                }
              })
            }
            socket.addEventListener("message", listener);
            socket.addEventListener("open", () => {
              if (!!socket && socket.readyState === WebSocket.OPEN) {
                fs_notification_socket.status = WebsocketStatus.connected;
                dispatch(websocketActions.wsFSDriveNotificationStatus(WebsocketStatus.connected));
                reconnect.resetBackoff();
              }
            });
            socket.addEventListener("close", (event: CloseEvent) => {
              fs_notification_socket.status = WebsocketStatus.disconnected;
              dispatch(websocketActions.wsFSDriveNotificationStatus(WebsocketStatus.disconnected));
              // NOTE: Handle reconnections
              if (!reconnect.manually_closed) {
                const delay = reconnect.getNextDelay();
                reconnect.timeout = setTimeout(connect, delay);
              }
            });
            socket.addEventListener("error", (event: Event) => {
              dispatch(uiActions.handleRequestErrors(
                new Message(DriveErrorMessages.error_fs_notification_socket, MessageHandlerDisplayType.logger),
                {
                  stack_message: DriveErrorMessages.error_fs_notification_socket,
                  stack_error: event
                }));
            });
          } catch (stack_error: unknown) {
            dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger),
              {
                stack_message: DriveErrorMessages.error_fs_notification_socket,
                stack_error
              }));
          }
        }

        // NOTE: Connect websocket on init
        connect();
        await cacheEntryRemoved;

        // NOTE: Remove logic for websocket reconnection
        reconnect.manually_closed = true;
        !!reconnect.timeout && clearTimeout(reconnect.timeout);
        (socket as WebSocket | null)?.close();
      },
    }),
    sendFSNotificationRequest: build.mutation<DriveRequest, DriveRequest>({
      queryFn: (data, api) => {
        const socket = fs_notification_socket?.socket;
        (!!socket && socket.readyState === WebSocket.OPEN) ? socket.send(data.envelope) :
          api.dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger), {
            stack_message: DriveErrorMessages.error_fs_notification_socket_not_ready,
            stack_error: { websocket_readyState: socket.readyState }
          }));
        return { data };
      }
    }),
    closeFSNotificationSocket: build.mutation<string, string>({
      queryFn: (data, api) => {
        const status = (api.getState() as RootState).websocket.fs_notification_ws_status;
        if (!!fs_notification_socket && (status === WebsocketStatus.connected || fs_notification_socket.socket.readyState === WebSocket.OPEN)) {
          fs_notification_socket.socket.close();
          api.dispatch(websocketActions.wsFSDriveNotificationStatus(WebsocketStatus.disconnecting));
        }
        return { data };
      }
    }),
    // --------------------------------------------------------------------------------
    // Drive Watch logs dedicated socket (Recent changes)
    // --------------------------------------------------------------------------------
    initializeWatchHooks: build.query<MessageRequest[], string>({
      queryFn: () => ({ data: [] }),
      async onCacheEntryAdded(arg, { dispatch, getState, cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        await cacheDataLoaded;
        let socket: WebSocket | null = null;
        //  Reconnection logic
        const reconnect = new WebSocketReconnect();
        // NOTE: connect websocket to server
        function connect() {
          // create a websocket connection when the cache subscription starts
          watch_socket = new DriveSocket();
          socket = watch_socket.socket;
          try {
            // NOTE: when data is received from the socket connection to the server,update our query result with the received message
            const listener = (event: MessageEvent) => {
              const data = event.data as ArrayBuffer;
              const serialized = new Uint8Array(data);
              const message = FSMessage.deserializeBinary(serialized);
              const request_id = message.getRequestId_asB64();
              const type = message.getType();
              updateCachedData((request: MessageRequest[]) => {
                request.push({
                  id: request_id,
                  message,
                  type
                });
              })
            }
            socket.addEventListener("message", listener);
            socket.addEventListener("open", () => {
              if (!!socket && socket.readyState === WebSocket.OPEN) {
                watch_socket.status = WebsocketStatus.connected;
                dispatch(websocketActions.wsDriveWatchStatus(WebsocketStatus.connected));
                reconnect.resetBackoff();
              }
            });
            socket.addEventListener("close", () => {
              watch_socket.status = WebsocketStatus.disconnected;
              dispatch(websocketActions.wsDriveWatchStatus(WebsocketStatus.disconnected));
              // NOTE: Handle reconnections
              if (!reconnect.manually_closed) {
                const delay = reconnect.getNextDelay();
                reconnect.timeout = setTimeout(connect, delay);
              }
            });
            socket.addEventListener("error", (event: Event) => {
              dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_watch_socket, MessageHandlerDisplayType.logger),
                {
                  stack_message: DriveErrorMessages.error_watch_socket,
                  stack_error: event
                }));
            });
          } catch (stack_error: unknown) {
            dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger), { stack_message: DriveErrorMessages.error_watch_socket, stack_error }));
          }
        }
        // NOTE: Connect websocket on init
        connect();
        await cacheEntryRemoved;

        // NOTE: Remove logic for websocket reconnection
        reconnect.manually_closed = true;
        !!reconnect.timeout && clearTimeout(reconnect.timeout);
        (socket as WebSocket | null)?.close();
      },
      providesTags: ["watch_ws"]
    }),
    sendWatchRequest: build.mutation<DriveRequest, DriveRequest>({
      queryFn: (data, api) => {
        const socket = watch_socket?.socket;
        if (!!socket && socket.readyState === WebSocket.OPEN) {
          socket.send(data.envelope);
          // Add DriveRequest to state
          const callbacks = (api.getState() as RootState).websocket.watch_ws_callbacks;
          const new_callbacks = callbacks.slice();
          new_callbacks.push(data);
          api.dispatch(websocketActions.setWsDriveWatchCallback(new_callbacks))
        } else {
          api.dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default, MessageHandlerDisplayType.logger),
            {
              stack_message: DriveErrorMessages.error_socket_not_ready.replace(MessageAnchors.actionType, "watch_socket"),
              stack_error: { websocket_readyState: socket.readyState }
            }));
        }

        return { data };
      }
    }),
    closeWatchSocket: build.mutation<string, string>({
      queryFn: (data, api) => {
        const status = (api.getState() as RootState).websocket.drive_watch_ws_status;
        if (!!watch_socket && (status === WebsocketStatus.connected || watch_socket.socket.readyState === WebSocket.OPEN)) {
          watch_socket.socket.close();
          api.dispatch(websocketActions.wsDriveWatchStatus(WebsocketStatus.disconnecting));
        }
        return { data };
      }
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as filesyncSocketApi };


// export { api as filesyncSocketApi };
export const {
  useInitializeDriveHooksQuery,
  useLazyInitializeDriveHooksQuery,
  useSendRequestMutation,
  useCloseSocketMutation,
  // Collection Server Filesync Notification Socket
  useInitializeNotificationHooksQuery,
  useLazyInitializeNotificationHooksQuery,
  useSendNotificationRequestMutation,
  useCloseNotificationSocketMutation,
  // Upload Socket
  useInitializeUploadHooksQuery,
  useLazyInitializeUploadHooksQuery,
  useSendUploadRequestMutation,
  useCloseUploadSocketMutation,
  // Filesync Notification Socket
  useInitializeFSNotificationHooksQuery,
  useLazyInitializeFSNotificationHooksQuery,
  useSendFSNotificationRequestMutation,
  useCloseFSNotificationSocketMutation,
  // Watch Logs Exclusive Socket
  useInitializeWatchHooksQuery,
  useLazyInitializeWatchHooksQuery,
  useSendWatchRequestMutation,
  useCloseWatchSocketMutation
} = injectedRtkApi;
